import type { MessageAttachment } from 'src/domain/message';
import type { SASInfo } from 'src/domain/user';
import { v4 as uuid } from 'uuid';

import {
    createImageThumbnail,
    createVideoThumbnail,
} from './attachmentThumbnails';

const HEIC_MIME_TYPES = new Set([
    'image/heic',
    'image/heif',
    'image/heic-sequence',
    'image/heif-sequence',
]);

export const DISPLAYABLE_IMAGE_MIME_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
]);

const HEIC_FILE_EXTENSION = /\.(heic|heif)$/i;

type MessageAttachmentStorage = {
    uploadImage: (
        file: File | Blob,
        name: string,
        sas: SASInfo,
    ) => Promise<unknown>;
};

export class MessageAttachmentConversionError extends Error {
    constructor() {
        super('Message attachment image conversion failed');
        this.name = 'MessageAttachmentConversionError';
    }
}

export const isHeicImage = (file: Pick<File, 'name' | 'type'>) =>
    HEIC_MIME_TYPES.has(file.type.toLowerCase()) ||
    HEIC_FILE_EXTENSION.test(file.name);

export const isAllowedMessageAttachment = (file: Pick<File, 'name' | 'type'>) =>
    isHeicImage(file) ||
    DISPLAYABLE_IMAGE_MIME_TYPES.has(file.type.toLowerCase()) ||
    file.type.startsWith('video/') ||
    file.type === 'application/pdf';

const getJpegFileName = (name: string) => {
    const baseName = name.replace(HEIC_FILE_EXTENSION, '');
    return `${baseName || 'message-image'}.jpg`;
};

export const normalizeMessageAttachment = async (file: File) => {
    if (!isHeicImage(file)) {
        return file;
    }

    try {
        const { default: heic2any } = await import('heic2any');
        const result = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9,
        });
        const jpeg = Array.isArray(result) ? result[0] : result;

        if (!jpeg) {
            throw new Error('HEIC decoder returned no image');
        }

        return new File([jpeg], getJpegFileName(file.name), {
            type: 'image/jpeg',
            lastModified: file.lastModified,
        });
    } catch {
        throw new MessageAttachmentConversionError();
    }
};

export const uploadMessageAttachments = async (
    files: File[],
    sas: SASInfo,
    storageService: MessageAttachmentStorage,
): Promise<MessageAttachment[]> => {
    const attachments: MessageAttachment[] = [];

    for (const sourceFile of files) {
        const file = await normalizeMessageAttachment(sourceFile);
        const blobName = `${uuid()}-${file.name}`;
        await storageService.uploadImage(file, blobName, sas);

        let thumbnailId: string | undefined;
        let thumbnailMimeType: string | undefined;

        if (file.type.startsWith('image/')) {
            const thumbnail = await createImageThumbnail(file);
            if (thumbnail) {
                thumbnailId = `${uuid()}-thumb.jpg`;
                await storageService.uploadImage(thumbnail, thumbnailId, sas);
                thumbnailMimeType = thumbnail.type;
            }
        } else if (file.type.startsWith('video/')) {
            const thumbnail = await createVideoThumbnail(file);
            if (thumbnail) {
                thumbnailId = `${uuid()}-thumb.jpg`;
                await storageService.uploadImage(thumbnail, thumbnailId, sas);
                thumbnailMimeType = thumbnail.type;
            }
        }

        attachments.push({
            id: blobName,
            name: file.name,
            mimeType: file.type,
            size: file.size,
            thumbnailId,
            thumbnailMimeType,
            thumbnailContainerName: thumbnailId ? sas.containerName : undefined,
        });
    }

    return attachments;
};
